import BottomSheet from "@components/BottomSheet/BottomSheet"
import NumericKeyboard from "@components/NumericKeyboard/NumericKeyboard"
import PrimaryButton from "@components/PrimaryButton/PrimaryButton"
import { useTranslation } from "react-i18next"
import { MINIMUM_TRANSACTION } from "@constants/transaction"
import { useNumericKeyboard } from "@hooks/useNumericKeyboard"
import { toCurrencyFormat } from "@lib/utils"
import {
    Container,
    BottomContainer,
    InputAmountContainer,
    InputTitle,
    Divider,
    BalanceContainer,
    BalanceText,
    BalanceAmount,
    AmountContainer,
    AmountText,
    MaxButtonContainer,
    MaxButtonText,
} from "./BottomSheetInputAmount.styles"
import { JIDRCurrency } from "@components/CurrencyIcon/CurrencyIcon"
import BottomSheetHeader from "@components/BottomSheetHeader/BottomSheetHeader"
import useBalance from "@hooks/useBalance"
import SkeletonLoader from "@components/SkeletonLoader/SkeletonLoader"
import { useCallback } from "react"

export const BottomSheetInputAmount = ({
    isOpen,
    onClose,
    onContinue,
}: {
    isOpen: boolean
    onClose: () => void
    onContinue: (amount: number) => void
}) => {
    const { t } = useTranslation()
    const { amount, setAmount, handleKeyboardPress } = useNumericKeyboard(0)
    const { data, isLoading } = useBalance()

    const handleMax = useCallback(() => {
        if (!isNaN(Number(data?.confirmed_balance)) && Number(data?.confirmed_balance) !== 0) {
            setAmount(Number(data.confirmed_balance))
        }
    }, [data?.confirmed_balance, setAmount])

    const maxHandler = (!isNaN(Number(data?.confirmed_balance)) && Number(data?.confirmed_balance) !== 0) ? handleMax : undefined

    return (
        <BottomSheet
            isOpen={isOpen}
            onClose={onClose}
            height="90%"
            header={
                <BottomSheetHeader title={t('components.bottomSheetInputAmount.transferTitle')} />
            }
        >
            <Container>
                <P2PTransferInputAmount
                    amount={amount}
                    currentBalance={data?.confirmed_balance}
                    minimumLimit={MINIMUM_TRANSACTION}
                    isLoading={isLoading}
                    onMax={maxHandler}
                />
                <BottomContainer>
                    <NumericKeyboard onChange={handleKeyboardPress} emptySpaceButton="thousand" />
                    <PrimaryButton
                        onClick={() => onContinue(amount)}
                        disabled={
                            amount === 0 ||
                            isLoading ||
                            amount < MINIMUM_TRANSACTION ||
                            data?.confirmed_balance === "unavailable" ||
                            amount > Number(data?.confirmed_balance)}
                    >
                        {t('components.bottomSheetInputAmount.continueButton')}
                    </PrimaryButton>
                </BottomContainer>
            </Container>
        </BottomSheet>
    )
}

const P2PTransferInputAmount = ({
    amount,
    currentBalance,
    minimumLimit,
    isLoading,
    onMax
}: {
    amount: number
    currentBalance: number | "unavailable"
    minimumLimit: number
    isLoading: boolean
    onMax?: () => void
}) => {
    const { t } = useTranslation()
    return (
        <InputAmountContainer>
            <InputTitle>{t('components.bottomSheetInputAmount.inputAmountTitle')}</InputTitle>
            <AmountDisplay
                amount={amount}
                onMax={onMax}
            />
            <Divider />
            <BalanceInformation
                currentBalance={currentBalance}
                minimumAmount={minimumLimit}
                isLoading={isLoading}
                errorMessage={
                    amount > 0 && amount < minimumLimit
                        ? t('p2p.errorMessages.minimumAmount', { amount: toCurrencyFormat(minimumLimit) })
                        : amount > Number(currentBalance || 0)
                            ? t('p2p.errorMessages.insufficientBalance')
                            : undefined
                }
            />
        </InputAmountContainer>
    )
}

const BalanceInformation = ({
    currentBalance,
    minimumAmount,
    isLoading,
    errorMessage
}: {
    currentBalance: number | "unavailable"
    minimumAmount: number
    isLoading: boolean
    errorMessage?: string
}) => {
    const { t } = useTranslation()
    return (
        <BalanceContainer>
            <BalanceText>
                {t('components.bottomSheetInputAmount.yourBalanceText')}
                {isLoading ? <SkeletonLoader /> : <BalanceAmount>
                    {currentBalance === "unavailable" ? t('p2p.unavailable') : `${toCurrencyFormat(currentBalance)} JIDR`}
                </BalanceAmount>}
            </BalanceText>
            <BalanceText>
                {`${t('components.bottomSheetInputAmount.minimumText')} ${toCurrencyFormat(minimumAmount)} JIDR`}
            </BalanceText>
            {errorMessage && <span style={{ color: "red" }}>{errorMessage}</span>}
        </BalanceContainer>
    )
}

const AmountDisplay = ({
    amount,
    onMax
}: {
    amount: number,
    onMax?: () => void
}) => {
    const { t } = useTranslation()
    return (
        <AmountContainer>
            <JIDRCurrency />
            <AmountText>
                {toCurrencyFormat(amount)}
            </AmountText>
            {(onMax !== undefined && onMax !== null) && <MaxButtonContainer
                onClick={onMax}
            >
                <MaxButtonText>{t('components.jidrInputCard.maxButton')}</MaxButtonText>
            </MaxButtonContainer>}
        </AmountContainer>
    )
}